import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as GrowthIcon,
  Groups as TeamIcon,
  Star as AwardIcon,
  CheckCircle as CheckIcon,
  Lightbulb as InnovationIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AboutUsPage = () => {
  const navigate = useNavigate();

  const stats = [
    { number: '500+', label: 'Active Businesses', icon: <BusinessIcon /> },
    { number: '2M+', label: 'Items Tracked', icon: <GrowthIcon /> },
    { number: '50+', label: 'Team Members', icon: <TeamIcon /> },
    { number: '99.9%', label: 'Uptime', icon: <AwardIcon /> },
  ];

  const values = [
    {
      title: 'Innovation First',
      description: 'We constantly innovate to provide cutting-edge solutions that help businesses stay ahead of the competition.',
      icon: <InnovationIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
    },
    {
      title: 'Customer Success',
      description: 'Your success is our success. We\'re committed to helping you achieve your business goals with our dedicated support.',
      icon: <SupportIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
    },
    {
      title: 'Data Security',
      description: 'We take data security seriously, implementing enterprise-grade security measures to protect your business information.',
      icon: <SecurityIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
    },
  ];

  const teamMembers = [
    {
      name: 'Alex Thompson',
      role: 'CEO & Founder',
      bio: '15+ years in retail technology, former VP at leading retail chain',
      avatar: 'AT',
    },
    {
      name: 'Sarah Chen',
      role: 'CTO',
      bio: 'Former Google engineer with expertise in scalable cloud solutions',
      avatar: 'SC',
    },
    {
      name: 'Michael Rodriguez',
      role: 'VP of Product',
      bio: '12+ years in product management, former consultant to Fortune 500 companies',
      avatar: 'MR',
    },
    {
      name: 'Emily Johnson',
      role: 'VP of Customer Success',
      bio: 'Dedicated to ensuring every customer achieves their business objectives',
      avatar: 'EJ',
    },
  ];

  const milestones = [
    {
      year: '2020',
      title: 'Company Founded',
      description: 'Started with a vision to revolutionize inventory management',
    },
    {
      year: '2021',
      title: 'First 100 Customers',
      description: 'Reached our first major milestone with small to medium businesses',
    },
    {
      year: '2022',
      title: 'Series A Funding',
      description: 'Raised $10M to expand our team and product capabilities',
    },
    {
      year: '2023',
      title: 'Enterprise Launch',
      description: 'Launched enterprise features serving large retail chains',
    },
    {
      year: '2024',
      title: 'Global Expansion',
      description: 'Expanding internationally with multilingual support and local partnerships',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            About DymenStock
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              opacity: 0.9,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}
          >
            Empowering businesses with smart inventory management solutions since 2020
          </Typography>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Our Mission
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
                To revolutionize how businesses manage their inventory, deliveries, and customer relationships through innovative technology solutions.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                We understand the unique challenges facing modern businesses - from managing complex inventory across multiple locations to coordinating delivery logistics. That's why we've built a comprehensive platform that addresses these specific needs.
              </Typography>
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Simplify complex inventory management" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Streamline delivery and logistics operations" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Enhance customer experience and satisfaction" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  backgroundColor: 'grey.100',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BusinessIcon sx={{ fontSize: 120, color: 'primary.main' }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Our Impact
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Numbers that showcase our commitment to excellence
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2, color: 'primary.main' }}>
                      {React.cloneElement(stat.icon, { sx: { fontSize: 48 } })}
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Our Values
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              The principles that guide everything we do
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    textAlign: 'center',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      {value.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Leadership Team
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Meet the experts behind DymenStock
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    height: '100%',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        backgroundColor: 'primary.main',
                        fontSize: '2rem',
                      }}
                    >
                      {member.avatar}
                    </Avatar>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {member.name}
                    </Typography>
                    <Typography variant="subtitle1" color="primary" sx={{ mb: 2 }}>
                      {member.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.bio}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Our Journey
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Key milestones in our company's growth
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {milestones.map((milestone, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    p: 3,
                    height: '100%',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
                    >
                      {milestone.year}
                    </Typography>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {milestone.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: 'primary.main',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Join Our Growing Community
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Become part of the inventory management revolution. Start your journey with DymenStock today.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: 'grey.300',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUsPage;
